import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeApprovalPlan, ChangeWorkGroup } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  DEFAULT_APPROVAL_SETTINGS,
  buildApprovalPlanPayload,
  buildSaveApprovalPlanConfirmDetails,
  draftFromPlan,
  labelApprovalTrack,
  validateApprovalPlan,
  visibleApprovalTracks,
  type ApprovalPlanDraft,
  type ApprovalSettings,
} from '../changeApprovalPlan';
import ChangeApprovalPlanEditor from './ChangeApprovalPlanEditor';

function planNeedsNewCommitteeRound(
  approvals?: Array<{ group_id?: number | null; status?: string | null; track?: string | null }> | null,
) {
  return (approvals || []).some(
    (item) =>
      (item.status === 'cancelado' || item.status === 'rejeitado' || item.status === 'reprovado') &&
      Boolean(item.group_id || item.track === 'criador_gestor' || item.track === 'gestao_mudancas'),
  );
}

interface Props {
  changeId: number;
  changeStatus?: string | null;
  approvals?: Array<{ group_id?: number | null; status?: string | null; track?: string | null }> | null;
  plan?: ChangeApprovalPlan | null;
  settings?: ApprovalSettings | null;
  canEdit: boolean;
  editLockedReason?: string;
  onUpdated: () => Promise<void> | void;
}

const ChangeApprovalPlanCard: React.FC<Props> = ({
  changeId,
  changeStatus,
  approvals,
  plan,
  settings,
  canEdit,
  editLockedReason,
  onUpdated,
}) => {
  const { showConfirm } = useModal();
  const resolvedSettings = settings || DEFAULT_APPROVAL_SETTINGS;
  const [draft, setDraft] = useState<ApprovalPlanDraft>(() => draftFromPlan(plan, resolvedSettings));
  const [areas, setAreas] = useState<ChangeWorkGroup[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const planKey = JSON.stringify(plan?.tracks || []);
  const settingsKey = `${Boolean(resolvedSettings.enable_riscos_impactos)}:${Boolean(
    resolvedSettings.enable_camud_go_no_go,
  )}:${resolvedSettings.enable_gestao_mudancas !== false}`;

  useEffect(() => {
    setDraft(draftFromPlan(plan, resolvedSettings));
  }, [planKey, settingsKey]);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    void changesApi.listWorkGroups({ only_active: true }).then((res) => {
      if (!cancelled) setAreas(res.items || []);
    });
    return () => {
      cancelled = true;
    };
  }, [canEdit]);

  const areaNamesById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const area of areas) {
      if (area.group_id != null) {
        map[area.group_id] = area.name || `Área #${area.group_id}`;
      }
    }
    return map;
  }, [areas]);

  const handleSave = async () => {
    const message = validateApprovalPlan(draft);
    if (message) {
      setError(message);
      return;
    }
    const ok = await showConfirm({
      title: 'Salvar plano de aprovação?',
      message:
        'Confira abaixo o comitê que ficará valendo. Você pode voltar sem alterar nada ou confirmar para salvar.',
      details: buildSaveApprovalPlanConfirmDetails(draft, areaNamesById),
      confirmLabel: 'Sim, salvar plano',
      cancelLabel: 'Não salvar',
    });
    if (!ok) return;
    setError(null);
    setSaving(true);
    try {
      await changesApi.replaceApprovalPlan(changeId, buildApprovalPlanPayload(draft).tracks);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar o plano de aprovação.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4" data-testid="change-approval-plan-card">
      <Card.Header className="fw-semibold">Quem aprova esta mudança</Card.Header>
      <Card.Body>
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {canEdit &&
        (changeStatus === 'autorizar' || changeStatus === 'agendado') &&
        planNeedsNewCommitteeRound(approvals) ? (
          <Alert variant="warning" className="small mb-2">
            Há aprovação reprovada ou cancelada no comitê. O histórico permanece. Escolha as áreas
            e o gestor e clique em <strong>Salvar plano de aprovação</strong> para disparar nova
            rodada. Sem isso a mudança não avança.
          </Alert>
        ) : null}
        {canEdit ? (
          <>
            <p className="text-muted small mb-2">
              Ligue os grupos de área e/ou o <strong>Gestor de mudanças</strong> (pessoa com a flag —
              sem escolher área). Ao salvar, as pendências aparecem na aba Aprovações.
            </p>
            <ChangeApprovalPlanEditor
              draft={draft}
              areas={areas}
              tracks={visibleApprovalTracks(resolvedSettings, draft)}
              onChange={(next) => {
                setError(null);
                setDraft(next);
              }}
            />
            <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar plano de aprovação'}
            </Button>
          </>
        ) : (
          <>
            {(plan?.tracks || []).length ? (
              (plan?.tracks || []).map((track) => (
                <div key={track.track} className="mb-3">
                  <div className="fw-semibold">{labelApprovalTrack(track.track)}</div>
                  <div className="text-muted">
                    {track.track === 'gestao_mudancas'
                      ? 'Admin ou usuário com flag Gestor de mudanças'
                      : [
                          ...(track.areas || []).map((area) => area.name || `Área #${area.group_id}`),
                          ...(track.people || []).map(
                            (person) =>
                              `${person.assignee_name || `Usuário #${person.assignee_id}`} · ${
                                person.group_name || `Área #${person.group_id}`
                              }`,
                          ),
                        ].join(', ') || '—'}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted mb-0">Nenhum grupo ou área configurado neste plano.</p>
            )}
            <p className="text-muted small mb-0">
              {editLockedReason ||
                'O plano de aprovação é definido no status Autorizar, enquanto ninguém tiver decidido. Depois cada área aprova ou reprova na aba Aprovações.'}
            </p>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ChangeApprovalPlanCard;
